import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Spinner, Form, Button, Modal, FormCheck } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getSuppliers, logSuppliersAnalysisAction } from '../../../services/SuppliersService';
import { getMaterials } from '../../../services/MaterialLogsService';

const HIDDEN_STORAGE_KEY = 'tabliya-suppliers-analysis-hidden';

function loadHiddenFromStorage() {
  try {
    const raw = localStorage.getItem(HIDDEN_STORAGE_KEY);
    if (!raw) return { materialLogIds: [], supplierTierKeys: [] };
    const parsed = JSON.parse(raw);
    return {
      materialLogIds: Array.isArray(parsed.materialLogIds) ? parsed.materialLogIds : [],
      supplierTierKeys: Array.isArray(parsed.supplierTierKeys) ? parsed.supplierTierKeys : [],
    };
  } catch {
    return { materialLogIds: [], supplierTierKeys: [] };
  }
}

function saveHiddenToStorage(hidden) {
  try {
    localStorage.setItem(HIDDEN_STORAGE_KEY, JSON.stringify(hidden));
  } catch (_) {}
}

/** Cell value: number (price) or { price, materialLogId } for material log entries (deletable). */
function getPrice(cell) {
  if (cell == null) return null;
  return typeof cell === 'object' && cell !== null && 'price' in cell ? cell.price : cell;
}
function getMaterialLogId(cell) {
  if (cell == null || typeof cell !== 'object') return null;
  return cell.materialLogId ?? null;
}

function addTier(byMaterial, mat, tierKey, tierLabel, q, unit, supplierName, price, opts = {}) {
  if (!byMaterial[mat]) {
    byMaterial[mat] = { tiers: [], tierOrder: [], supplierPrices: {}, supplierMeta: {} };
  }
  const rec = byMaterial[mat];
  if (!rec.tierOrder.includes(tierKey)) {
    rec.tierOrder.push(tierKey);
    rec.tiers.push({ tierKey, tierLabel, quantity: q, unit });
  }
  if (!rec.supplierPrices[supplierName]) rec.supplierPrices[supplierName] = {};
  rec.supplierPrices[supplierName][tierKey] = opts.materialLogId != null
    ? { price, materialLogId: opts.materialLogId }
    : price;
  if (opts.supplierId != null) rec.supplierMeta[supplierName] = { type: 'supplier', id: opts.supplierId };
}

/**
 * Build comparison data from both Suppliers and Material Logs (materials page).
 * hiddenFromAnalysis: { materialLogIds: number[], supplierTierKeys: string[] } — entries to exclude from this view only.
 */
function buildComparisonByMaterial(suppliers, materialLogs, hiddenFromAnalysis = { materialLogIds: [], supplierTierKeys: [] }, unknownLabel = 'Unknown', materialsLogLabel = 'Materials log') {
  const byMaterial = {};
  const hiddenIds = new Set(hiddenFromAnalysis.materialLogIds || []);
  const hiddenTiers = new Set(hiddenFromAnalysis.supplierTierKeys || []);

  (suppliers || []).forEach((supplier) => {
    const name = (supplier.name || '').trim() || unknownLabel;
    const supplierId = supplier.id;
    (supplier.prices || []).forEach((p) => {
      const mat = (p.material_name || '').trim();
      if (!mat) return;
      const q = parseFloat(p.quantity);
      const unit = (p.unit || 'unit').trim() || 'unit';
      const price = parseFloat(p.price_per_unit);
      if (isNaN(q) || q <= 0 || isNaN(price) || price < 0) return;
      const tierKey = `${q}_${unit}`;
      if (hiddenTiers.has(`${supplierId}_${mat}_${tierKey}`)) return;
      const tierLabel = `${q} ${unit}`;
      addTier(byMaterial, mat, tierKey, tierLabel, q, unit, name, price, { supplierId });
    });
  });

  (materialLogs || []).forEach((m) => {
    if (hiddenIds.has(m.id)) return;
    const mat = (m.material_name || '').trim();
    if (!mat) return;
    const q = parseFloat(m.quantity_purchased);
    const unit = (m.unit || 'unit').trim() || 'unit';
    const pricePerUnit = m.price_per_unit != null ? parseFloat(m.price_per_unit) : (parseFloat(m.price) / (q || 1));
    if (isNaN(q) || q <= 0 || isNaN(pricePerUnit) || pricePerUnit < 0) return;
    const supplierName = (m.source || '').trim() || materialsLogLabel;
    const tierKey = `${q}_${unit}`;
    const tierLabel = `${q} ${unit}`;
    addTier(byMaterial, mat, tierKey, tierLabel, q, unit, supplierName, pricePerUnit, { materialLogId: m.id });
  });

  Object.keys(byMaterial).forEach((mat) => {
    byMaterial[mat].tiers.sort((a, b) => {
      if (a.quantity !== b.quantity) return a.quantity - b.quantity;
      return (a.unit || '').localeCompare(b.unit || '');
    });
    byMaterial[mat].tierOrder = byMaterial[mat].tiers.map((t) => t.tierKey);
  });

  return byMaterial;
}

function formatPrice(value) {
  if (value == null || isNaN(value)) return '—';
  return `€${Number(value).toFixed(2)}`;
}

function formatPriceWithUnit(value, unit) {
  if (value == null || isNaN(value)) return '—';
  const u = (unit || 'unit').trim() || 'unit';
  return `€${Number(value).toFixed(2)}/${u}`;
}

export default function SuppliersAnalysis() {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [materialLogs, setMaterialLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchMaterial, setSearchMaterial] = useState('');
  const [hiddenFromAnalysis, setHiddenFromAnalysis] = useState(() => loadHiddenFromStorage());
  const [confirmRemove, setConfirmRemove] = useState(null); // { type, idOrKey } to remove from analysis
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedToImport, setSelectedToImport] = useState({ materialLogs: [], supplierTiers: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [suppliersRes, materialsRes] = await Promise.all([
          getSuppliers(),
          getMaterials().catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setSuppliers(suppliersRes.data || []);
          setMaterialLogs(materialsRes.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err.response?.data?.error || err.message || t('suppliersAnalysis.errors.load');
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const comparison = useMemo(
    () => buildComparisonByMaterial(suppliers, materialLogs, hiddenFromAnalysis, t('suppliersAnalysis.unknownSupplier'), t('suppliersAnalysis.materialsLogFallback')),
    [suppliers, materialLogs, hiddenFromAnalysis, t]
  );

  const materialNames = useMemo(() => Object.keys(comparison).sort(), [comparison]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suppliersRes, materialsRes] = await Promise.all([
        getSuppliers(),
        getMaterials().catch(() => ({ data: [] })),
      ]);
      setSuppliers(suppliersRes.data || []);
      setMaterialLogs(materialsRes.data || []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('suppliersAnalysis.errors.load');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleHideFromAnalysis = (type, idOrKey) => {
    if (type === 'material_log') {
      setHiddenFromAnalysis((prev) => {
        const next = {
          materialLogIds: [...(prev.materialLogIds || []), idOrKey],
          supplierTierKeys: prev.supplierTierKeys || [],
        };
        saveHiddenToStorage(next);
        return next;
      });
      logSuppliersAnalysisAction('hide_from_suppliers_analysis', { type: 'material_log', material_log_id: idOrKey }).catch(() => {});
      toast.success(t('suppliersAnalysis.toasts.removedFromMaterials'));
    } else {
      setHiddenFromAnalysis((prev) => {
        const next = {
          materialLogIds: prev.materialLogIds || [],
          supplierTierKeys: [...(prev.supplierTierKeys || []), idOrKey],
        };
        saveHiddenToStorage(next);
        return next;
      });
      logSuppliersAnalysisAction('hide_from_suppliers_analysis', { type: 'supplier_tier', tier_key: idOrKey }).catch(() => {});
      toast.success(t('suppliersAnalysis.toasts.removedFromSuppliers'));
    }
    setConfirmRemove(null);
  };

  const handleResetHidden = () => {
    setHiddenFromAnalysis({ materialLogIds: [], supplierTierKeys: [] });
    saveHiddenToStorage({ materialLogIds: [], supplierTierKeys: [] });
    logSuppliersAnalysisAction('reset_suppliers_analysis_hidden', {}).catch(() => {});
    toast.success(t('suppliersAnalysis.toasts.allRestored'));
  };

  /** Build list of hidden entries with details for the import modal. */
  const hiddenEntriesList = useMemo(() => {
    const hiddenIds = new Set(hiddenFromAnalysis.materialLogIds || []);
    const hiddenTiers = new Set(hiddenFromAnalysis.supplierTierKeys || []);
    const list = { materials: [], suppliers: [] };

    // From Material Logs
    (materialLogs || []).forEach((m) => {
      if (!hiddenIds.has(m.id)) return;
      const q = parseFloat(m.quantity_purchased);
      const unit = (m.unit || 'unit').trim() || 'unit';
      const pricePerUnit = m.price_per_unit != null ? parseFloat(m.price_per_unit) : (parseFloat(m.price) / (q || 1));
      if (isNaN(q) || q <= 0 || isNaN(pricePerUnit)) return;
      list.materials.push({
        id: m.id,
        material_name: (m.material_name || '').trim(),
        source: (m.source || '').trim() || t('suppliersAnalysis.materialsLogFallback'),
        quantity: q,
        unit,
        price_per_unit: pricePerUnit,
      });
    });

    // From Suppliers
    (suppliers || []).forEach((supplier) => {
      const supplierId = supplier.id;
      const supplierName = (supplier.name || '').trim() || t('suppliersAnalysis.unknownSupplier');
      (supplier.prices || []).forEach((p) => {
        const mat = (p.material_name || '').trim();
        if (!mat) return;
        const q = parseFloat(p.quantity);
        const unit = (p.unit || 'unit').trim() || 'unit';
        const price = parseFloat(p.price_per_unit);
        if (isNaN(q) || q <= 0 || isNaN(price)) return;
        const tierKey = `${supplierId}_${mat}_${q}_${unit}`;
        if (!hiddenTiers.has(tierKey)) return;
        list.suppliers.push({
          key: tierKey,
          supplierId,
          supplierName,
          material_name: mat,
          quantity: q,
          unit,
          price_per_unit: price,
        });
      });
    });

    return list;
  }, [materialLogs, suppliers, hiddenFromAnalysis, t]);

  const handleOpenImportModal = () => {
    setSelectedToImport({ materialLogs: [], supplierTiers: [] });
    setShowImportModal(true);
  };

  const handleToggleImportMaterialLog = (id) => {
    setSelectedToImport((prev) => ({
      ...prev,
      materialLogs: prev.materialLogs.includes(id)
        ? prev.materialLogs.filter((x) => x !== id)
        : [...prev.materialLogs, id],
    }));
  };

  const handleToggleImportSupplierTier = (key) => {
    setSelectedToImport((prev) => ({
      ...prev,
      supplierTiers: prev.supplierTiers.includes(key)
        ? prev.supplierTiers.filter((x) => x !== key)
        : [...prev.supplierTiers, key],
    }));
  };

  const handleConfirmImport = () => {
    setHiddenFromAnalysis((prev) => {
      const next = {
        materialLogIds: (prev.materialLogIds || []).filter((id) => !selectedToImport.materialLogs.includes(id)),
        supplierTierKeys: (prev.supplierTierKeys || []).filter((key) => !selectedToImport.supplierTiers.includes(key)),
      };
      saveHiddenToStorage(next);
      return next;
    });
    const total = selectedToImport.materialLogs.length + selectedToImport.supplierTiers.length;
    logSuppliersAnalysisAction('restore_to_suppliers_analysis', {
      material_log_count: selectedToImport.materialLogs.length,
      supplier_tier_count: selectedToImport.supplierTiers.length,
      total,
    }).catch(() => {});
    toast.success(t('suppliersAnalysis.toasts.restoredCount', { count: total }));
    setShowImportModal(false);
    setSelectedToImport({ materialLogs: [], supplierTiers: [] });
  };

  const hasHiddenEntries = (hiddenFromAnalysis.materialLogIds?.length || 0) + (hiddenFromAnalysis.supplierTierKeys?.length || 0) > 0;

  const filteredMaterialNames = useMemo(() => {
    const q = (searchMaterial || '').trim().toLowerCase();
    if (!q) return materialNames;
    return materialNames.filter((m) => m.toLowerCase().includes(q));
  }, [materialNames, searchMaterial]);

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="text-primary" />
          <p className="mt-2 text-muted mb-0">{t('suppliers.loadingSuppliers')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card>
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <h4 className="heading mb-0">{t('suppliersAnalysis.title')}</h4>
                  <span className="text-muted">{t('suppliersAnalysis.subtitle')}</span>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Control
                    type="text"
                    placeholder={t('suppliersAnalysis.searchPlaceholder')}
                    value={searchMaterial}
                    onChange={(e) => setSearchMaterial(e.target.value)}
                    style={{ maxWidth: 260 }}
                  />
                  <Button variant="outline-primary" size="sm" onClick={handleOpenImportModal} title={t('suppliersAnalysis.importButtonTitle')}>
                    <i className="fa-solid fa-download me-1" /> {t('suppliersAnalysis.importButton')}
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {materialNames.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p className="mb-0">{t('suppliersAnalysis.noData')}</p>
                  <p className="small mb-0">{t('suppliersAnalysis.noDataHelp')}</p>
                </div>
              ) : filteredMaterialNames.length === 0 ? (
                <div className="text-center py-4 text-muted">{t('suppliersAnalysis.noSearchMatch')}</div>
              ) : (
                <div className="suppliers-analysis">
                  {filteredMaterialNames.map((materialName) => {
                    const rec = comparison[materialName];
                    const supplierNames = Object.keys(rec.supplierPrices);

                    return (
                      <Card key={materialName} className="mb-4 border shadow-sm">
                        <Card.Header className="bg-light py-3 suppliers-analysis-card-header">
                          <h5 className="mb-0 text-dark">{materialName}</h5>
                          <span className="text-muted small">{t('suppliersAnalysis.cardSubtitle')}</span>
                        </Card.Header>
                        <Card.Body className="p-0">
                          <Table responsive bordered className="mb-0 suppliers-analysis-table">
                            <thead>
                              <tr>
                                <th className="bg-light" style={{ width: 140 }}>{t('common.quantity')}</th>
                                {supplierNames.map((s) => (
                                    <th key={s} className="bg-light text-center">{s}</th>
                                ))}
                                <th className="bg-light text-center" style={{ width: 100 }}>{t('suppliersAnalysis.columns.cheapest')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rec.tiers.map((tier) => {
                                const cells = supplierNames.map((s) => rec.supplierPrices[s][tier.tierKey]);
                                const prices = cells.map((c) => getPrice(c));
                                const validPrices = prices.filter((v) => v != null && !isNaN(v));
                                const minPrice = validPrices.length ? Math.min(...validPrices) : null;
                                const countAtMin = minPrice != null
                                  ? prices.filter((v) => v != null && !isNaN(v) && v === minPrice).length
                                  : 0;
                                const isTied = countAtMin > 1;
                                return (
                                  <tr key={tier.tierKey}>
                                    <td className="fw-medium" data-label={t('common.quantity')}>{tier.tierLabel}</td>
                                    {supplierNames.map((s) => {
                                      const cell = rec.supplierPrices[s][tier.tierKey];
                                      const price = getPrice(cell);
                                      const materialLogId = getMaterialLogId(cell);
                                      const isCheapest = price != null && !isNaN(price) && price === minPrice;
                                      return (
                                        <td key={s} className="text-center align-middle" data-label={s}>
                                          <span className="d-inline-flex align-items-center gap-1 flex-wrap justify-content-center">
                                            {price != null && !isNaN(price) ? (
                                              <>
                                                <span className={isCheapest ? 'text-success fw-bold' : ''}>
                                                  {formatPriceWithUnit(price, tier.unit)}
                                                  {isCheapest && isTied && (
                                                    <span className="ms-1 badge bg-secondary">{t('suppliersAnalysis.badges.same')}</span>
                                                  )}
                                                  {isCheapest && !isTied && (
                                                    <span className="ms-1 badge bg-success">{t('suppliersAnalysis.badges.best')}</span>
                                                  )}
                                                </span>
                                                {(materialLogId != null || (rec.supplierMeta?.[s]?.type === 'supplier' && rec.supplierMeta?.[s]?.id != null)) && (
                                                  <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-danger"
                                                    title={t('suppliersAnalysis.removeTitle')}
                                                    onClick={() => setConfirmRemove({
                                                      type: materialLogId != null ? 'material_log' : 'supplier_tier',
                                                      idOrKey: materialLogId != null ? materialLogId : `${rec.supplierMeta[s].id}_${materialName}_${tier.tierKey}`,
                                                    })}
                                                  >
                                                    <i className="fa-solid fa-times" />
                                                  </Button>
                                                )}
                                              </>
                                            ) : (
                                              <span className="text-muted">—</span>
                                            )}
                                          </span>
                                        </td>
                                      );
                                    })}
                                    <td className="text-center" data-label={t('suppliersAnalysis.columns.cheapest')}>
                                      {minPrice != null ? (
                                        <span className="text-success fw-semibold">{formatPriceWithUnit(minPrice, tier.unit)}</span>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}

              {materialNames.length > 0 && (
                <div className="mt-3 text-muted small">
                  {t('suppliersAnalysis.showingCount', { shown: filteredMaterialNames.length, total: materialNames.length })}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Remove from analysis confirmation — in-page modal */}
          <Modal show={confirmRemove != null} onHide={() => setConfirmRemove(null)} centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('suppliersAnalysis.removeModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {t('suppliersAnalysis.removeModal.body')}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setConfirmRemove(null)}>{t('common.cancel')}</Button>
              <Button variant="primary" onClick={() => confirmRemove && handleHideFromAnalysis(confirmRemove.type, confirmRemove.idOrKey)}>
                {t('suppliersAnalysis.removeModal.confirmButton')}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Import hidden entries modal */}
          <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t('suppliersAnalysis.importModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <p className="text-muted mb-3">{t('suppliersAnalysis.importModal.selectPrompt')}</p>

              {hiddenEntriesList.materials.length === 0 && hiddenEntriesList.suppliers.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0">{t('suppliersAnalysis.importModal.noHidden')}</p>
                </div>
              ) : (
                <>
                  {hiddenEntriesList.materials.length > 0 && (
                    <div className="mb-4">
                      <h6 className="mb-2">{t('suppliersAnalysis.importModal.fromMaterials', { count: hiddenEntriesList.materials.length })}</h6>
                      <div className="border rounded p-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {hiddenEntriesList.materials.map((entry) => (
                          <FormCheck
                            key={entry.id}
                            type="checkbox"
                            id={`import-mat-${entry.id}`}
                            checked={selectedToImport.materialLogs.includes(entry.id)}
                            onChange={() => handleToggleImportMaterialLog(entry.id)}
                            label={
                              <span>
                                <strong>{entry.material_name}</strong> — {entry.source}: {entry.quantity} {entry.unit} @ {formatPriceWithUnit(entry.price_per_unit, entry.unit)}
                              </span>
                            }
                            className="mb-2"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {hiddenEntriesList.suppliers.length > 0 && (
                    <div>
                      <h6 className="mb-2">{t('suppliersAnalysis.importModal.fromSuppliers', { count: hiddenEntriesList.suppliers.length })}</h6>
                      <div className="border rounded p-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {hiddenEntriesList.suppliers.map((entry) => (
                          <FormCheck
                            key={entry.key}
                            type="checkbox"
                            id={`import-supp-${entry.key}`}
                            checked={selectedToImport.supplierTiers.includes(entry.key)}
                            onChange={() => handleToggleImportSupplierTier(entry.key)}
                            label={
                              <span>
                                <strong>{entry.material_name}</strong> — {entry.supplierName}: {entry.quantity} {entry.unit} @ {formatPriceWithUnit(entry.price_per_unit, entry.unit)}
                              </span>
                            }
                            className="mb-2"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowImportModal(false)}>{t('common.cancel')}</Button>
              <Button
                variant="primary"
                onClick={handleConfirmImport}
                disabled={selectedToImport.materialLogs.length === 0 && selectedToImport.supplierTiers.length === 0}
              >
                {t('suppliersAnalysis.importModal.importSelected', { count: selectedToImport.materialLogs.length + selectedToImport.supplierTiers.length })}
              </Button>
            </Modal.Footer>
          </Modal>

        </div>
      </div>
    </div>
  );
}
